// Core imports
import { ShopCore } from "@/abilities/shop/types/shop-core";

// Router and constants
import { SHOP_LIGHT_CART_ALIAS } from "@/abilities/shop/config/constants";
import { ShopLight } from "@/abilities/shop/types/shop-light";
import { router } from "@/app/router";
import { ShopArticleContext } from "@/shared/environment/widgets/widget-context";
import { storeToRefs } from "pinia";
import { getWidgetItemFromInfoItem } from "../../shop-core/helper/get-widget-item-from-info-item";
import { useShopLightCartApi } from "../api/cart";
import { useSendMailApi } from "../api/send-mails";
import { useShopInfoApi } from "../api/shop-articles";
import { useCheckoutStore } from "../store/checkout.store";

/**
 * Composable providing Shop Light functionality
 * This is a lightweight implementation of the shop interface
 * @returns Collection of shop-related actions and utilities
 */

export const useShopLight = (): ShopLight.IActions => {
    const { articleInfo, mechanicInfo } = useShopInfoApi();
    const { addItem, deleteItem, getCart, updateItem } = useShopLightCartApi();
    const checkoutStore = useCheckoutStore();
    const { basketRequestForMail } = storeToRefs(checkoutStore);
    const { sendMail } = useSendMailApi();

    // setArticles(mockArticles);

    /**
     * Redirects user to the Shop Light interface
     * Uses internal routing instead of external URLs
     */
    async function redirectToBasket(): Promise<void> {
        // Use the router to navigate to the Shop Light cart alias
        // HACK - use additional / because the ability router factory adds always a slash before aliases
        await router.push({ name: `/${SHOP_LIGHT_CART_ALIAS}` });
    }

    /**
     * Adds a part to the user's shopping basket
     * @param articleId - The ID of the article to add
     * @param quantity - The quantity to add
     */
    async function addPartToShopBasket(
        articleId: string,
        quantity: number,
        context?: ShopArticleContext
    ): Promise<void> {
        const cartItem: ShopLight.CartItem = {
            cartItemId: {
                articleId: articleId,
                partLocation: context
                    ? {
                          mechanicId: context?.mechanicId,
                      }
                    : undefined,
            },
            quantity,
        };
        await addItem(cartItem);
    }

    /**
     * Retrieves shop item information for a specific article
     * @param articleId - The ID of the article to get info for
     * @returns Promise with item information or null if not found
     */
    async function getShopItemInfoForArticle(
        articleId: string,
        context?: ShopArticleContext
    ): Promise<ShopCore.WidgetItem | null> {
        if (context?.mechanicId) {
            const item = await mechanicInfo(context.mechanicId);
            if (!item) return null;
            return getWidgetItemFromInfoItem(item);
        }
        const item = await articleInfo(articleId);
        if (!item) return null;
        return getWidgetItemFromInfoItem(item);
    }

    /**
     * Gets the count of items in the basket
     * @returns Promise with the count of items in the basket
     */
    async function getBasketCount(): Promise<number> {
        const basket = await getCart();
        return basket ? basket.map((item) => item.quantity ?? 0).reduce((a, b) => a + b, 0) : 0;
    }

    /**
     * Empties the user's shopping basket
     * @returns Promise that resolves when the basket is emptied
     */
    async function emptyBasket(): Promise<void> {
        const basket = await getCart();
        if (basket) {
            for (const item of basket) {
                await deleteItem(item);
            }
        }
    }

    /**
     * Sends a cart request email with the current basket & checkout contents
     * @returns {Promise<string>} Job ID for tracking the email sending process
     * @throws {ShopLight.SendMailError} when GraphQL errors occur (e.g., rate limit, validation)
     */
    async function sendCartRequestEmail(): Promise<string> {
        try {
            // Send email request for the cart
            const data = basketRequestForMail.value;
            const jobId = await sendMail(data);
            return jobId;
        } catch (error) {
            console.error("Error sending cart request email:", error);
            throw error;
        }
    }

    // Return the public API
    return {
        // Core shopping functionality
        addPartToShopBasket,
        getShopItemInfoForArticle,

        // Basket operations
        getBasket: getCart,
        getBasketCount,
        upsertArticle: updateItem,
        deleteArticle: deleteItem,
        emptyBasket,
        sendCartRequestEmail,

        // Navigation and client access
        redirectToBasket,
    };
};
